import { useEffect, useRef, useState } from 'react';
import { getEmailTemplate, previewEmailTemplate, saveEmailTemplate } from '../api/client';
import { X, Bold, List, Plus } from 'lucide-react';

const PREVIEW_DELAY_MS = 400;

// Wrap the selected text in **…**, or insert **bold** at the cursor.
function wrapBold(text, start, end) {
  const picked = text.slice(start, end) || 'bold';
  return { text: `${text.slice(0, start)}**${picked}**${text.slice(end)}`, cursor: start + picked.length + 4 };
}

// Turn the selected lines (or the cursor's line) into "- " bullet lines.
function bulletLines(text, start, end) {
  const from = text.lastIndexOf('\n', start - 1) + 1;
  const toBreak = text.indexOf('\n', end);
  const to = toBreak === -1 ? text.length : toBreak;
  const lines = text.slice(from, to).split('\n').map((line) => (line.startsWith('- ') ? line : `- ${line}`));
  const replaced = lines.join('\n');
  return { text: text.slice(0, from) + replaced + text.slice(to), cursor: from + replaced.length };
}

/**
 * The invitation email in a popup before it goes out: edit the subject and message (simple
 * formatting), see the real email beside it, then confirm. The edit is saved as the template
 * used from now on. The link button and the signature are added by the server and cannot be
 * edited away.
 */
export default function EmailTemplateModal({
  title, summary, confirmLabel, invitationId, recipients, companies, skipped, onConfirm, onClose,
}) {
  const [template, setTemplate] = useState(null);
  const [current, setCurrent] = useState(0);
  const people = recipients || [];
  const shownPerson = people[Math.min(current, Math.max(0, people.length - 1))] || null;
  const previewFor = shownPerson
    ? { invitation_id: shownPerson.invitation_id, contact_id: shownPerson.contact_id }
    : invitationId;
  const previewKey = shownPerson ? `${shownPerson.invitation_id || ''}:${shownPerson.contact_id || ''}` : invitationId;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getEmailTemplate()
      .then((t) => { if (!cancelled) { setTemplate(t); setSubject(t.subject); setBody(t.body); } })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!template) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      previewEmailTemplate(subject, body, previewFor)
        .then((p) => { if (!cancelled) { setPreview(p); setError(''); } })
        .catch((e) => { if (!cancelled) setError(e.message); });
    }, PREVIEW_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // previewKey stands for previewFor, which is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, subject, body, previewKey]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  function edit(change) {
    const el = bodyRef.current;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    const next = change(body, start, end);
    setBody(next.text);
    requestAnimationFrame(() => {
      if (el) { el.focus(); el.setSelectionRange(next.cursor, next.cursor); }
    });
  }

  function insert(token) {
    edit((text, start, end) => ({ text: text.slice(0, start) + token + text.slice(end), cursor: start + token.length }));
  }

  async function confirm() {
    setBusy(true);
    setError('');
    try {
      if (!template || subject !== template.subject || body !== template.body) {
        setTemplate(await saveEmailTemplate(subject, body));
      }
      await onConfirm();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const changedFromDefault = template && (subject !== template.default_subject || body !== template.default_body);

  return (
    <>
      <div className="overlay show corpus-email-overlay" data-testid="email-popup-backdrop" onClick={busy ? undefined : onClose} />
      <div className="drawer corpus-modal corpus-email-modal show" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">{title}</div>
            {summary && <div className="corpus-line">{summary}</div>}
            {companies && (
              <>
                <div className="corpus-line">
                  To <strong>{people.length} {people.length === 1 ? 'person' : 'people'}</strong> at{' '}
                  <strong>{companies.length} {companies.length === 1 ? 'company' : 'companies'}</strong>
                  {template && template.sender ? ` · from ${template.sender.name} <${template.sender.email}>` : ''}
                </div>
                <div className="corpus-email-chips">
                  {companies.map((c) => <span key={c.name}>{c.name} · {c.count}</span>)}
                </div>
                {skipped && skipped.length > 0 && (
                  <div className="corpus-hint">Not emailed: {skipped.map((s) => `${s.company} (${s.reason.toLowerCase()})`).join(', ')}.</div>
                )}
              </>
            )}
          </div>
          <button type="button" className="drawer-close" onClick={onClose} disabled={busy} aria-label="Close"><X size={18} aria-hidden="true" /></button>
        </div>
        <div className="drawer-body">
          {!template ? (
            error ? <div className="corpus-error" role="alert">{error}</div> : <div className="corpus-line">Loading the email…</div>
          ) : (
            <div className="corpus-email-editor">
              <div className="corpus-email-edit">
                <label className="corpus-field">
                  Subject
                  <input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} />
                </label>
                <div className="corpus-email-toolbar" role="toolbar" aria-label="Formatting">
                  <button type="button" onClick={() => edit(wrapBold)} disabled={busy}><Bold size={15} aria-hidden="true" /> Bold</button>
                  <button type="button" onClick={() => edit(bulletLines)} disabled={busy}><List size={15} aria-hidden="true" /> List</button>
                  <button type="button" onClick={() => insert('{first_name}')} disabled={busy}><Plus size={14} aria-hidden="true" /> First name</button>
                  <button type="button" onClick={() => insert('{company}')} disabled={busy}><Plus size={14} aria-hidden="true" /> Company</button>
                </div>
                <textarea ref={bodyRef} aria-label="Message" value={body} onChange={(e) => setBody(e.target.value)} disabled={busy} rows={18} />
                <p className="corpus-hint">
                  **bold**, a line starting with "- " is a bullet, a blank line starts a new paragraph. The
                  &quot;Get your estimate&quot; button with the person&apos;s own link and the signature are added after the message.
                </p>
                {changedFromDefault && (
                  <button type="button" className="corpus-link-button" disabled={busy}
                    onClick={() => { setSubject(template.default_subject); setBody(template.default_body); }}>
                    Back to the original text
                  </button>
                )}
              </div>
              <div className="corpus-email-preview">
                {people.length > 1 && (
                  <div className="corpus-email-person" aria-live="polite">
                    <span>Preview as</span>
                    <button type="button" aria-label="Previous person" onClick={() => setCurrent((i) => Math.max(0, i - 1))} disabled={current === 0}>‹</button>
                    <strong>{shownPerson.name} · {shownPerson.company}</strong>
                    <button type="button" aria-label="Next person" onClick={() => setCurrent((i) => Math.min(people.length - 1, i + 1))} disabled={current >= people.length - 1}>›</button>
                    <span className="corpus-muted">{current + 1} of {people.length}</span>
                  </div>
                )}
                {preview ? (
                  <>
                    <div className="corpus-line"><strong>To:</strong> {preview.to}{preview.sender ? <> · <strong>From:</strong> {preview.sender.name} &lt;{preview.sender.email}&gt;</> : null}</div>
                    <div className="corpus-line"><strong>Subject:</strong> {preview.subject}</div>
                    <iframe title="Email preview" sandbox="" srcDoc={preview.html} />
                  </>
                ) : <div className="corpus-line">Building the preview…</div>}
              </div>
            </div>
          )}
          {template && error && <div className="corpus-error" role="alert">{error}</div>}
          <div className="corpus-actions corpus-email-actions">
            <button type="button" className="approve" onClick={confirm} disabled={busy || !template}>
              {busy ? 'Sending…' : confirmLabel}
            </button>
            <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <span className="corpus-hint">Your edit is saved as the email used from now on.</span>
          </div>
        </div>
      </div>
    </>
  );
}
